import { Injectable } from "@angular/core";
import { Actions, concatLatestFrom, createEffect } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { PlatformService } from "@obg/ngx.global.evo";
import { ignoreEmpty } from "@obg/rxjs";
import { filter, map } from "rxjs/operators";

import { batchActions } from "../extensions/actions-batcher";
import { NgrxUtilsState } from "../model";

import { StateCacheAction } from "./state-cache.action";
import { StateCacheStrategy } from "./state-cache.model";
import { StateCacheSelector } from "./state-cache.selector";
import { isCachedGet, isCachedGetSuccess, restoreOriginalAction } from "./state-cache.util";

@Injectable({
	providedIn: "root",
})
export class StateCacheEffect {
	cachedGetIndefinite$ = createEffect(() =>
		this.actions$.pipe(
			filter(isCachedGet),
			filter(({ cacheDetail }) => cacheDetail.cacheStrategy === StateCacheStrategy.Indefinite),
			concatLatestFrom((action) => this.store.select(StateCacheSelector.isFetched(action.cacheDetail.cacheKey))),
			map(([{ cacheDetail, ...originalAction }, isFetched]) => {
				if (isFetched) {
					return cacheDetail.fallbackAction;
				}
				return restoreOriginalAction(originalAction, cacheDetail.originalType);
			}),
			ignoreEmpty()
		)
	);

	cachedGetExpiry$ = createEffect(() =>
		this.actions$.pipe(
			filter(isCachedGet),
			filter(({ cacheDetail }) => cacheDetail.cacheStrategy === StateCacheStrategy.Expiry),
			concatLatestFrom(({ cacheDetail }) =>
				this.store.select(StateCacheSelector.isExpired(cacheDetail.cacheKey, cacheDetail.expirationMs || 0))
			),
			map(([{ cacheDetail, ...originalAction }, isExpired]) => {
				if (!isExpired) {
					return cacheDetail.fallbackAction;
				}
				return restoreOriginalAction(originalAction, cacheDetail.originalType);
			}),
			ignoreEmpty()
		)
	);

	serverCachedGet$ = createEffect(() =>
		this.actions$.pipe(
			filter(isCachedGet),
			filter(({ cacheDetail }) => cacheDetail.cacheStrategy === StateCacheStrategy.Server),
			concatLatestFrom(({ cacheDetail }) =>
				this.store.select(StateCacheSelector.isFetchedOnServer(cacheDetail.cacheKey))
			),
			map(([{ cacheDetail, ...originalAction }, isFetchedOnServer]) => {
				const key = cacheDetail.cacheKey;
				const isServer = this.platformService.isServer;

				if (!isServer && isFetchedOnServer) {
					return batchActions(
						// request already made on server - ignore and clear cache on client so next dispatch is executed
						this.stateCacheAction.clearCache(key),
						cacheDetail.fallbackAction
					);
				}

				return restoreOriginalAction(originalAction, cacheDetail.originalType);
			})
		)
	);

	getSuccess$ = createEffect(() =>
		this.actions$.pipe(
			filter(isCachedGetSuccess),
			map((action) => this.stateCacheAction.setFetched(action.cacheKey, this.platformService.isServer))
		)
	);

	constructor(
		private actions$: Actions,
		private store: Store<NgrxUtilsState>,
		private stateCacheAction: StateCacheAction,
		private platformService: PlatformService
	) {}
}
